import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Api from '../../Api';
import { withAuth } from '../../hoc/Auth';
import { Wrapper } from '../../hoc/Layout';
import classes from './styles.css';

const POLL_MS = 7000;

const STATUSES = [
    { value: '', label: 'Все статусы' },
    { value: 'open', label: 'Открыто' },
    { value: 'pending_user', label: 'Ожидает ответа' },
    { value: 'closed', label: 'Закрыто' },
];

/* ---------- icons ---------- */

const IconChevronDown = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const IconChevronUp = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 15l-6-6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const IconPaperclip = ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M21.44 11.05l-9.19 9.19a5.5 5.5 0 01-7.78-7.78l9.19-9.19a3.5 3.5 0 014.95 4.95l-8.83 8.83a1.5 1.5 0 01-2.12-2.12l7.41-7.41"
            stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
        />
    </svg>
);

const IconSend = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M22 2l-7 20-4-9-9-4 20-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const IconFile = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M14 2v6h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const IconEdit = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const IconTrash = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const IconEmptyChat = () => (
    <svg width="56" height="56" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M8 13.5C8 11.01 10.01 9 12.5 9h23C37.99 9 40 11.01 40 13.5v17C40 33 37.99 35 35.5 35H22l-8 8v-8h-1.5C10.01 35 8 33 8 30.5v-17z"
            fill="#6366f1" opacity="0.18"
        />
        <path
            d="M8 13.5C8 11.01 10.01 9 12.5 9h23C37.99 9 40 11.01 40 13.5v17C40 33 37.99 35 35.5 35H22l-8 8v-8h-1.5C10.01 35 8 33 8 30.5v-17z"
            stroke="#6366f1" strokeWidth="2" strokeLinejoin="round"
        />
        <circle cx="17" cy="22" r="1.6" fill="#6366f1" />
        <circle cx="24" cy="22" r="1.6" fill="#6366f1" />
        <circle cx="31" cy="22" r="1.6" fill="#6366f1" />
    </svg>
);

/* ---------- helpers ---------- */

function statusLabel(s) {
    if (s === 'open') return 'Открыто';
    if (s === 'pending_user') return 'Ожидает ответа';
    if (s === 'closed') return 'Закрыто';
    return s;
}

function statusClass(s) {
    if (s === 'open') return classes.StatusOpen;
    if (s === 'pending_user') return classes.StatusPendingUser;
    if (s === 'closed') return classes.StatusClosed;
    return classes.StatusOpen;
}

function fmtFull(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit',
    });
}

function fmtRelative(iso) {
    if (!iso) return '';
    const dt = new Date(iso);
    const sec = Math.max(1, Math.floor((Date.now() - dt.getTime()) / 1000));
    if (sec < 60) return `${sec}с`;
    if (sec < 3600) return `${Math.floor(sec / 60)}м`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}ч`;
    if (sec < 86400 * 7) return `${Math.floor(sec / 86400)}д`;
    return dt.toLocaleDateString('ru-RU');
}

function fmtSize(b) {
    if (!b) return '';
    if (b < 1024) return `${b} б`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} КБ`;
    return `${(b / 1024 / 1024).toFixed(1)} МБ`;
}

function avatarInitials(name) {
    if (!name) return '?';
    const parts = String(name).trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
}

function pickLatestUpdated(messages, fallback) {
    let latest = fallback;
    (messages || []).forEach((m) => {
        if (!latest || m.updated_at > latest) latest = m.updated_at;
    });
    return latest;
}

/* ---------- main ---------- */

const TicketsAdminPage = withAuth(({ token }) => {
    const api = useMemo(() => (token ? new Api({ token }) : null), [token]);
    const history = useHistory();
    const { id: routeId } = useParams();
    const activeId = routeId ? parseInt(routeId, 10) : null;

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const pageSize = 30;
    const [filters, setFilters] = useState({ status: '', q: '', only_unread: false });

    const [ticket, setTicket] = useState(null);
    const [merchant, setMerchant] = useState(null);
    const [messages, setMessages] = useState([]);
    const [draftBody, setDraftBody] = useState('');
    const [draftFiles, setDraftFiles] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [editingBody, setEditingBody] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [showTerminals, setShowTerminals] = useState(false);
    const lastUpdatedRef = useRef(null);
    const fileInputRef = useRef(null);
    const chatBodyRef = useRef(null);

    const loadList = useCallback(() => {
        if (!api) return;
        const params = {
            status: filters.status || undefined,
            q: filters.q || undefined,
            only_unread: filters.only_unread ? 1 : undefined,
            page,
            page_size: pageSize,
        };
        api.adminListTickets(params).then((data) => {
            setItems(data.items || []);
            setTotal(data.total || 0);
        }).catch(() => {});
    }, [api, page, filters]);

    useEffect(() => { loadList(); }, [loadList]);

    useEffect(() => {
        if (!api) return undefined;
        const i = setInterval(loadList, 30000);
        return () => clearInterval(i);
    }, [api, loadList]);

    const loadTicketInitial = useCallback(async (id) => {
        if (!api || !id) return;
        try {
            const data = await api.adminGetTicket(id);
            setTicket(data.ticket);
            setMerchant(data.merchant);
            setMessages(data.messages || []);
            lastUpdatedRef.current = pickLatestUpdated(data.messages, null);
            api.adminMarkTicketRead(id).catch(() => {});
        } catch (e) {
            setError('Не удалось загрузить обращение.');
        }
    }, [api]);

    useEffect(() => {
        if (!activeId) {
            setTicket(null); setMerchant(null); setMessages([]);
            lastUpdatedRef.current = null;
            return;
        }
        loadTicketInitial(activeId);
    }, [activeId, loadTicketInitial]);

    useEffect(() => {
        if (!api || !activeId) return undefined;
        const poll = async () => {
            try {
                const data = await api.adminGetTicketMessages(activeId, lastUpdatedRef.current);
                if (!(data.messages || []).length) return;
                setMessages((prev) => {
                    const map = new Map();
                    prev.forEach((m) => map.set(m.id, m));
                    (data.messages || []).forEach((m) => map.set(m.id, m));
                    return Array.from(map.values()).sort((a, b) => a.id - b.id);
                });
                lastUpdatedRef.current = pickLatestUpdated(data.messages, lastUpdatedRef.current);
            } catch (e) { /* silent */ }
        };
        const i = setInterval(poll, POLL_MS);
        return () => clearInterval(i);
    }, [api, activeId]);

    useEffect(() => {
        if (chatBodyRef.current) chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }, [messages]);

    const openTicket = (t) => {
        if (activeId === t.id) return;
        setError(null);
        setEditingId(null); setEditingBody('');
        setDraftBody(''); setDraftFiles([]);
        setShowTerminals(false);
        history.push(`/admin/tickets/${t.id}`);
    };

    const onPickFiles = (filesList) => {
        const arr = Array.from(filesList || []);
        if (draftFiles.length + arr.length > 10) { setError('Не более 10 файлов.'); return; }
        for (const f of arr) {
            if (f.size > 25 * 1024 * 1024) { setError(`"${f.name}" больше 25 МБ.`); return; }
        }
        setError(null);
        setDraftFiles([...draftFiles, ...arr]);
    };

    const removeDraftFile = (idx) => {
        const next = [...draftFiles]; next.splice(idx, 1); setDraftFiles(next);
    };

    const submitMessage = async () => {
        if (!ticket) return;
        if (!draftBody.trim() && draftFiles.length === 0) return;
        setSending(true); setError(null);
        try {
            await api.adminPostTicketMessage(ticket.id, draftBody, draftFiles);
            setDraftBody(''); setDraftFiles([]);
            await loadTicketInitial(ticket.id);
            loadList();
        } catch (e) {
            setError((e.response && e.response.data && e.response.data.message) || 'Не удалось отправить.');
        } finally { setSending(false); }
    };

    const startEdit = (m) => { setEditingId(m.id); setEditingBody(m.body); };
    const cancelEdit = () => { setEditingId(null); setEditingBody(''); };
    const saveEdit = async () => {
        try {
            await api.adminEditTicketMessage(ticket.id, editingId, editingBody);
            cancelEdit();
            await loadTicketInitial(ticket.id);
        } catch (e) { setError('Не удалось сохранить.'); }
    };

    const deleteMsg = async (m) => {
        if (!window.confirm('Удалить это сообщение?')) return;
        try { await api.adminDeleteTicketMessage(ticket.id, m.id); await loadTicketInitial(ticket.id); }
        catch (e) { setError('Не удалось удалить.'); }
    };

    const deleteAttachment = async (a) => {
        if (!window.confirm(`Удалить вложение "${a.filename}"?`)) return;
        try { await api.adminDeleteTicketAttachment(ticket.id, a.id); await loadTicketInitial(ticket.id); }
        catch (e) { setError('Не удалось удалить вложение.'); }
    };

    const changeStatus = async (newStatus) => {
        try {
            await api.adminChangeTicketStatus(ticket.id, newStatus);
            await loadTicketInitial(ticket.id);
            loadList();
        } catch (e) { setError('Не удалось изменить статус.'); }
    };

    const deleteTicket = async () => {
        if (!window.confirm('Удалить обращение полностью? Пользователь его больше не увидит.')) return;
        try {
            await api.adminDeleteTicket(ticket.id);
            history.push('/admin/tickets');
            loadList();
        } catch (e) { setError('Не удалось удалить.'); }
    };

    /* ---------- render ---------- */

    const renderEmpty = () => (
        <div className={classes.Empty}>
            <div className={classes.EmptyIllustration}><IconEmptyChat /></div>
            <div className={classes.EmptyTitle}>Выберите обращение</div>
            <div className={classes.EmptyText}>
                Кликните на любую карточку справа, чтобы открыть переписку с мерчантом.
            </div>
        </div>
    );

    const renderChat = () => {
        if (!ticket) return renderEmpty();
        const terminals = (merchant && merchant.terminals) || [];
        const isClosed = ticket.status === 'closed';
        const merchantName = (merchant && merchant.username) || 'Пользователь';

        return (
            <Fragment>
                <div className={classes.ChatHeader}>
                    <div className={classes.ChatHeaderTop}>
                        <div className={classes.ChatTitle}>{ticket.subject}</div>
                        <span className={`${classes.StatusChip} ${classes.StatusChipBig} ${classes.StatusChipHeader}`}>
                            {statusLabel(ticket.status)}
                        </span>
                        <div className={classes.HeaderActions}>
                            {isClosed ? (
                                <button className={`${classes.HeaderBtn} ${classes.HeaderBtnPrimary}`} onClick={() => changeStatus('open')}>
                                    Открыть заново
                                </button>
                            ) : (
                                <button className={classes.HeaderBtn} onClick={() => changeStatus('closed')}>
                                    Закрыть
                                </button>
                            )}
                            <button className={`${classes.HeaderBtn} ${classes.HeaderBtnDanger}`} onClick={deleteTicket}>
                                Удалить
                            </button>
                        </div>
                    </div>

                    <div className={classes.MerchantBar}>
                        <div className={classes.MerchantAvatar}>{avatarInitials(merchantName)}</div>
                        <div className={classes.MerchantInfo}>
                            <span className={classes.Name}>{merchantName}</span>
                            <span className={classes.Email}>{merchant && merchant.email}</span>
                        </div>
                        <div className={classes.HeaderMeta}>
                            <span>зарегистрирован {merchant && merchant.registered_at ? fmtFull(merchant.registered_at) : '—'}</span>
                            {merchant && merchant.profile_url && (
                                <Fragment>
                                    <span className={classes.HeaderMetaSep} />
                                    <a className={classes.ProfileLink} href={merchant.profile_url}>Открыть профиль</a>
                                </Fragment>
                            )}
                            {terminals.length > 0 && (
                                <Fragment>
                                    <span className={classes.HeaderMetaSep} />
                                    <button className={classes.TerminalsToggle} onClick={() => setShowTerminals(!showTerminals)}>
                                        {terminals.length} {terminals.length === 1 ? 'терминал' : 'терминалов / проектов'}
                                        {showTerminals ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />}
                                    </button>
                                </Fragment>
                            )}
                        </div>
                    </div>

                    {showTerminals && terminals.length > 0 && (
                        <div className={classes.TerminalsPopup}>
                            {terminals.map((t) => (
                                <div key={t.terminal_id}>
                                    {t.terminal_name || `Терминал #${t.terminal_id}`}
                                    {t.merchant_name && <span style={{ opacity: 0.6 }}> · {t.merchant_name}</span>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div ref={chatBodyRef} className={classes.ChatBody}>
                    {messages.length === 0 ? (
                        <div className={classes.Empty}>
                            <div className={classes.EmptyIllustration}><IconEmptyChat /></div>
                            <div className={classes.EmptyText}>Сообщений пока нет.</div>
                        </div>
                    ) : (
                        messages.map((m) => {
                            const isMine = m.author_type === 'admin';
                            return (
                                <div key={m.id} className={`${classes.MsgRow} ${isMine ? classes.MsgRowMine : ''}`}>
                                    {!isMine && (
                                        <div className={`${classes.Avatar} ${classes.AvatarUser}`}>{avatarInitials(merchantName)}</div>
                                    )}
                                    <div className={classes.MsgGroup}>
                                        <div className={`${classes.MsgBubble} ${isMine ? classes.MsgBubbleMine : ''} ${m.deleted ? classes.MsgBubbleDeleted : ''}`}>
                                            {!m.deleted && (
                                                <div className={classes.MsgActions}>
                                                    <button className={classes.MsgActionBtn} onClick={() => startEdit(m)} title="Редактировать">
                                                        <IconEdit />
                                                    </button>
                                                    <button className={`${classes.MsgActionBtn} ${classes.Danger}`} onClick={() => deleteMsg(m)} title="Удалить">
                                                        <IconTrash />
                                                    </button>
                                                </div>
                                            )}
                                            {editingId === m.id ? (
                                                <div className={classes.EditForm}>
                                                    <textarea
                                                        className={classes.EditFormArea}
                                                        value={editingBody}
                                                        onChange={(e) => setEditingBody(e.target.value)}
                                                    />
                                                    <div className={classes.EditActions}>
                                                        <button className={classes.EditSave} onClick={saveEdit}>Сохранить</button>
                                                        <button className={classes.EditCancel} onClick={cancelEdit}>Отмена</button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <Fragment>
                                                    {m.deleted ? 'сообщение удалено' : m.body}
                                                    {(m.attachments || []).map((a) =>
                                                        a.purged ? (
                                                            <span key={a.id} className={classes.AttachmentPurged}>
                                                                <IconFile /> файл удалён по сроку
                                                            </span>
                                                        ) : (
                                                            <span key={a.id} className={classes.AttachmentInline}>
                                                                <button
                                                                    type="button"
                                                                    className={classes.AttachmentChip}
                                                                    onClick={() => api.adminDownloadTicketAttachment(ticket.id, a.id, a.filename)}
                                                                    title={`${a.filename} · ${fmtSize(a.size_bytes)}`}
                                                                >
                                                                    <IconFile /> {a.filename}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className={classes.AttachmentDelete}
                                                                    onClick={() => deleteAttachment(a)}
                                                                    title="Удалить вложение"
                                                                >×</button>
                                                            </span>
                                                        )
                                                    )}
                                                </Fragment>
                                            )}
                                        </div>
                                        <div className={classes.MsgFooter}>
                                            <span className={`${classes.AuthorTag} ${isMine ? classes.AuthorTagAdmin : ''}`}>
                                                {isMine ? 'Поддержка' : merchantName}
                                            </span>
                                            <span>·</span>
                                            <span>{fmtFull(m.created_at)}</span>
                                            {m.edited && <span className={classes.MsgEditedTag}>· изменено</span>}
                                        </div>
                                    </div>
                                    {isMine && (
                                        <div className={`${classes.Avatar} ${classes.AvatarSupport}`}>S</div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>

                {isClosed ? (
                    <div className={classes.ClosedNote}>
                        Обращение закрыто. Нажмите «Открыть заново», чтобы продолжить переписку.
                    </div>
                ) : (
                    <div className={classes.Composer}>
                        {error && <div className={classes.ComposerError}>{error}</div>}
                        {draftFiles.length > 0 && (
                            <div className={classes.AttachPreviewList}>
                                {draftFiles.map((f, idx) => (
                                    <div key={idx} className={classes.AttachPreview}>
                                        <IconFile /> {f.name} · {fmtSize(f.size)}
                                        <button className={classes.AttachRemove} onClick={() => removeDraftFile(idx)}>×</button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <textarea
                            className={classes.ComposerArea}
                            placeholder="Ответ пользователю..."
                            value={draftBody}
                            onChange={(e) => setDraftBody(e.target.value)}
                        />
                        <div className={classes.ComposerToolbar}>
                            <button
                                type="button"
                                className={classes.IconBtn}
                                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                title="Прикрепить файл"
                            >
                                <IconPaperclip />
                            </button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                className={classes.HiddenFile}
                                onChange={(e) => onPickFiles(e.target.files)}
                            />
                            <button className={classes.SendBtn} disabled={sending} onClick={submitMessage}>
                                <IconSend />
                                {sending ? 'Отправка...' : 'Отправить'}
                            </button>
                        </div>
                    </div>
                )}
            </Fragment>
        );
    };

    return (
        <Wrapper>
            <div className={classes.Outer}>
                <div className={classes.Page}>
                    <div className={classes.MainPanel}>
                        {activeId ? renderChat() : renderEmpty()}
                    </div>

                    <div className={classes.SidePanel}>
                        <div className={classes.SideHeader}>
                            <div className={classes.SideTitle}>Обращения</div>
                            <div className={classes.SideSubtitle}>Всего: {total}</div>
                        </div>

                        <div className={classes.Filters}>
                            <select
                                value={filters.status}
                                onChange={(e) => { setPage(1); setFilters({ ...filters, status: e.target.value }); }}
                            >
                                {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </select>
                            <div className={classes.SearchWrap}>
                                <input
                                    type="text"
                                    placeholder="Поиск по теме..."
                                    value={filters.q}
                                    onChange={(e) => { setPage(1); setFilters({ ...filters, q: e.target.value }); }}
                                />
                            </div>
                            <label className={classes.FiltersRow}>
                                <input
                                    type="checkbox"
                                    checked={filters.only_unread}
                                    onChange={(e) => { setPage(1); setFilters({ ...filters, only_unread: e.target.checked }); }}
                                />
                                только непрочитанные
                            </label>
                        </div>

                        <div className={classes.ListLabel}>Список обращений</div>
                        <div className={classes.TicketList}>
                            {items.length === 0 ? (
                                <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 12, padding: 24 }}>
                                    Ничего не найдено
                                </div>
                            ) : (
                                items.map((t) => {
                                    const isActive = activeId === t.id;
                                    const accountName = (t.account && t.account.username) || '';
                                    const accountEmail = (t.account && t.account.email) || '';
                                    return (
                                        <div
                                            key={t.id}
                                            className={`${classes.TicketCard} ${isActive ? classes.TicketCardActive : ''}`}
                                            onClick={() => openTicket(t)}
                                        >
                                            <div className={classes.TicketCardTop}>
                                                <div className={`${classes.TicketCardSubject} ${t.unread_for_admin ? classes.TicketCardSubjectUnread : ''}`}>
                                                    {t.subject}
                                                </div>
                                                {t.unread_for_admin && <span className={classes.UnreadDot} />}
                                            </div>
                                            <div className={classes.TicketCardEmail}>
                                                {accountName}{accountName && accountEmail ? ' · ' : ''}{accountEmail}
                                            </div>
                                            <div className={classes.TicketCardMeta}>
                                                <span className={`${classes.StatusChip} ${statusClass(t.status)}`}>
                                                    {statusLabel(t.status)}
                                                </span>
                                                <span>{fmtRelative(t.last_user_message_at || t.last_admin_message_at || t.updated_at)}</span>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        <div className={classes.Pagination}>
                            <span>стр. {page}</span>
                            <div className={classes.PageNav}>
                                <button className={classes.PageBtn} disabled={page === 1} onClick={() => setPage(page - 1)}>←</button>
                                <button className={classes.PageBtn} disabled={page * pageSize >= total} onClick={() => setPage(page + 1)}>→</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Wrapper>
    );
});

export default TicketsAdminPage;



// WEBPACK FOOTER //
// ./src/PagesOfAdministrator/Tickets/index.js